import Button from "@components/Button"
import OrderSummary from "@components/OrderSummary"
import PaymentPending from "@components/PaymentPending"
import TermsCheckbox from "@components/TermsCheckbox"
import {
  resolveConfidenceVisitorId,
  VISITOR_ID_QUERY_PARAM,
} from "@helpers/confidenceVisitorId"
import { trackSubscriptionEvent } from "@helpers/facebookTracking"
import {
  getConfirmPaymentErrorMessage,
  getFinishedPaymentIntentTarget,
  isPendingPaymentIntentStatus,
  isTerminalPaymentIntentError,
} from "@helpers/stripeErrors"
import { LockClosedIcon, ShieldCheckIcon } from "@heroicons/react/24/outline"
import * as Sentry from "@sentry/react"
import { PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js"
import { PaymentIntent, StripeError } from "@stripe/stripe-js"
import { useCallback, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useLocation, useNavigate } from "react-router-dom"
import { Plan } from "src/types"

const MAX_LOAD_RETRIES = 2

interface Props {
  clientSecret: string
  currency: string
  fullPrice: number
  plan: Plan
  stripeFirstPriceId: string
  stripeNextPriceId: string
  userEmail: string | undefined
}

export default function CheckoutForm({
  clientSecret,
  currency: _currency,
  fullPrice,
  plan,
  stripeFirstPriceId,
  stripeNextPriceId,
  userEmail,
}: Props) {
  void _currency // Reserved for future use

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [isTermsAccepted, setIsTermsAccepted] = useState<boolean>(false)
  // NOTE: confirmPayment rejects with an IntegrationError until the Payment
  // Element has mounted and emitted `ready` (Sentry PRODUCTION-SN, -SG).
  const [isPaymentElementReady, setIsPaymentElementReady] =
    useState<boolean>(false)
  // NOTE: Remounting the Payment Element (a new key) retries a load that
  // failed, e.g. on a network error, with the same client secret. After
  // MAX_LOAD_RETRIES the retry reloads the page, which also loads Stripe.js
  // again.
  const [elementKey, setElementKey] = useState<number>(0)
  const [hasLoadFailed, setHasLoadFailed] = useState<boolean>(false)
  const [isLookupDone, setIsLookupDone] = useState<boolean>(false)
  const [loadRetries, setLoadRetries] = useState<number>(0)
  const hasReportedLoadError = useRef<boolean>(false)
  const [message, setMessage] = useState<string | undefined>(undefined)
  const [paymentIntent, setPaymentIntent] = useState<PaymentIntent | undefined>(
    undefined,
  )

  const elements = useElements()
  const location = useLocation()
  const navigate = useNavigate()
  const stripe = useStripe()
  const { t } = useTranslation()

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (!elements || !stripe || !isPaymentElementReady) return

    setIsSubmitting(true)
    setMessage(undefined)

    const origin = window.location.origin
    const pathname = "/checkout/payment-completion"
    const urlSearchParams = new URLSearchParams(location.search)

    // Confirming payment may hand the browser to Stripe (redirect payment
    // methods), and coming back is a fresh page load that has to find the visitor id again. Measured on Lingo
    // web, 23% of purchases find nothing there and mint a new id, so the
    // purchase lands on a visitor with no funnel behind it. Passing it through
    // the return URL is the only carrier the browser cannot drop.
    //
    // Cleared before writing, and written with `set` rather than `append`,
    // because these params start as a copy of the current URL's. A visitor
    // arriving on an attacker-crafted `?cnfd_visitor_id=` would otherwise carry
    // it into the return URL, and since `URLSearchParams.get` returns the first
    // of a duplicated key, theirs would win over ours on the way back. The
    // delete also covers the case where we have no id to send: an inbound one
    // must not survive on its own.
    urlSearchParams.delete(VISITOR_ID_QUERY_PARAM)

    const confidenceVisitorId = resolveConfidenceVisitorId()
    if (confidenceVisitorId) {
      urlSearchParams.set(VISITOR_ID_QUERY_PARAM, confidenceVisitorId)
    }

    const returnUrl = `${origin}${pathname}?${urlSearchParams.toString()}`

    let error: StripeError | undefined
    let confirmedPaymentIntent: PaymentIntent | undefined

    try {
      // NOTE: `if_required`: cards, Apple Pay and Google Pay (3D Secure
      // included) finish on this page, and the completion page is opened
      // without a page load. That keeps the Supabase session, which lives in
      // memory only when the browser blocks storage. Payment methods that
      // must redirect (e.g. PayPal) still come back to `returnUrl`.
      ;({ error, paymentIntent: confirmedPaymentIntent } =
        await stripe.confirmPayment({
          elements,
          confirmParams: { return_url: returnUrl },
          redirect: "if_required",
        }))
    } catch (integrationError) {
      // Thrown (not returned) when Stripe.js can't use the element, e.g. it
      // was unmounted meanwhile. Used to be an unhandled rejection.
      Sentry.captureException(integrationError)
      setIsSubmitting(false)
      setMessage(t("components.checkoutForm.somethingWentWrong"))

      return
    }

    if (error || !confirmedPaymentIntent) {
      setIsSubmitting(false)
      if (error) setMessage(getConfirmPaymentErrorMessage(error, t))

      return
    }

    // NOTE: The same parameters Stripe adds to `returnUrl` after a redirect.
    urlSearchParams.set("payment_intent", confirmedPaymentIntent.id)
    urlSearchParams.set(
      "payment_intent_client_secret",
      confirmedPaymentIntent.client_secret ?? clientSecret,
    )
    urlSearchParams.set("redirect_status", confirmedPaymentIntent.status)

    navigate(`${pathname}?${urlSearchParams.toString()}`)
  }

  // Sends the user on when the PaymentIntent is finished. `true` when it did.
  const leaveIfFinished = useCallback(
    (intent: PaymentIntent | undefined) => {
      const target = getFinishedPaymentIntentTarget(intent?.status)

      if (intent && target === "plans") {
        navigate("/checkout/plans", { replace: true })

        return true
      }

      if (intent && target === "completion") {
        const params = new URLSearchParams(location.search)
        params.set("payment_intent", intent.id)
        params.set(
          "payment_intent_client_secret",
          intent.client_secret ?? clientSecret,
        )
        params.set("redirect_status", intent.status)
        navigate(`/checkout/payment-completion?${params.toString()}`, {
          replace: true,
        })

        return true
      }

      return false
    },
    [clientSecret, location.search, navigate],
  )

  const showLastPaymentError = useCallback(
    (intent: PaymentIntent | undefined) => {
      if (!intent?.last_payment_error) return

      if (
        intent.last_payment_error.code ===
        "payment_intent_authentication_failure"
      ) {
        setMessage(t("components.checkoutForm.paymentFailed"))
      } else {
        setMessage(t("components.checkoutForm.somethingWentWrong"))
      }
    },
    [t],
  )

  // A pending payment settled: on to completion or the plans when it is
  // finished, back to the form when the bank refused it.
  const handleSettled = useCallback(
    (intent: PaymentIntent) => {
      setPaymentIntent(intent)

      if (leaveIfFinished(intent)) return

      showLastPaymentError(intent)
    },
    [leaveIfFinished, showLastPaymentError],
  )

  useEffect(() => {
    if (!clientSecret || !stripe) {
      return
    }

    stripe
      .retrievePaymentIntent(clientSecret)
      .then(({ paymentIntent }) => {
        setPaymentIntent(paymentIntent)

        // NOTE: The back button, a reload or an old history entry opens this
        // page for a payment that is already finished (PRODUCTION-W1): the
        // Payment Element refuses it, so send the user on instead.
        if (leaveIfFinished(paymentIntent)) return

        showLastPaymentError(paymentIntent)
      })
      .catch((error) => Sentry.captureException(error))
      .finally(() => setIsLookupDone(true))
  }, [clientSecret, leaveIfFinished, showLastPaymentError, stripe])

  useEffect(() => {
    if (!paymentIntent) return

    const trackEvent = async () => {
      await trackSubscriptionEvent({
        currency: paymentIntent.currency,
        isPurchase: false,
        planPrice: paymentIntent.amount,
        stripeFirstPriceId,
        stripeNextPriceId,
      })
    }

    trackEvent().catch((error) => console.error(error))
  }, [paymentIntent, stripeFirstPriceId, stripeNextPriceId])

  useEffect(() => {
    const resetSubmittingState = () => {
      setIsSubmitting(false)
    }

    window.addEventListener("pageshow", resetSubmittingState)
    document.addEventListener("visibilitychange", resetSubmittingState)

    return () => {
      window.removeEventListener("pageshow", resetSubmittingState)
      document.removeEventListener("visibilitychange", resetSubmittingState)
    }
  }, [])

  if (isPendingPaymentIntentStatus(paymentIntent?.status)) {
    return (
      <PaymentPending clientSecret={clientSecret} onSettled={handleSettled} />
    )
  }

  return (
    <form className="flex flex-col gap-5" onSubmit={handleSubmit}>
      <OrderSummary
        fullPrice={fullPrice}
        paymentIntent={paymentIntent}
        plan={plan}
      />

      <div className="vm-card animate-fade-up p-4 [animation-delay:120ms]">
        {/* NOTE: Not before the lookup says the intent is payable (a pending
        one takes no Payment Element). Without Stripe.js there is no lookup. */}
        {(isLookupDone || !stripe) && (
          <PaymentElement
            key={elementKey}
            onLoadError={({ error }) => {
              // NOTE: The button stays disabled, so say why. A finished
              // payment is not an error: the lookup sends the user on.
              if (isTerminalPaymentIntentError(error)) {
                // NOTE: Don't wait for the lookup above (it may have failed
                // or not be back yet): a terminal intent never works, so look
                // again and, if its status is still unknown, get a fresh one
                // on the plans page rather than leave a dead form.
                stripe
                  ?.retrievePaymentIntent(clientSecret)
                  .then(({ paymentIntent: intent }) => {
                    if (isPendingPaymentIntentStatus(intent?.status)) {
                      setPaymentIntent(intent)
                    } else if (!leaveIfFinished(intent)) {
                      navigate("/checkout/plans", { replace: true })
                    }
                  })
                  .catch(() => navigate("/checkout/plans", { replace: true }))
              } else {
                // NOTE: Once per page, so retrying doesn't flood Sentry.
                if (!hasReportedLoadError.current) {
                  hasReportedLoadError.current = true
                  Sentry.captureException(
                    new Error(`Payment Element failed to load: ${error.type}`),
                    { extra: { code: error.code, message: error.message } },
                  )
                }
                setHasLoadFailed(true)
              }
              setMessage(t("components.checkoutForm.somethingWentWrong"))
            }}
            onReady={() => setIsPaymentElementReady(true)}
            options={{
              defaultValues: {
                billingDetails: {
                  email: userEmail,
                },
              },
              layout: {
                defaultCollapsed: false,
                type: "tabs",
              },
              paymentMethodOrder: [
                "apple_pay",
                "link",
                "card",
                "paypal",
                "google_pay",
              ],
            }}
          />
        )}
      </div>

      <div className="animate-fade-up space-y-4 [animation-delay:240ms]">
        <TermsCheckbox
          fullPrice={fullPrice}
          isTermsAccepted={isTermsAccepted}
          paymentIntent={paymentIntent}
          plan={plan}
          setIsTermsAccepted={setIsTermsAccepted}
        />

        {message && (
          <p className="animate-fade-in rounded-vm-md bg-vm-red-soft p-3 text-center text-sm font-semibold text-vm-red-text">
            {message}
          </p>
        )}

        {hasLoadFailed && (
          <Button
            onClick={() => {
              if (loadRetries >= MAX_LOAD_RETRIES) {
                window.location.reload()

                return
              }

              setLoadRetries(loadRetries + 1)
              setHasLoadFailed(false)
              setMessage(undefined)
              setElementKey(elementKey + 1)
            }}
            title={t("pages.error.tryAgain")}
            variant="secondary"
          />
        )}

        <Button
          className={isTermsAccepted ? "animate-pulse-cta" : ""}
          disabled={
            !elements || !stripe || !isPaymentElementReady || !isTermsAccepted
          }
          submitting={isSubmitting}
          title={t("pages.checkout.payment.submitPayment")}
          type="submit"
        />

        {!isTermsAccepted && (
          <p className="text-center text-xs text-vm-muted">
            {t("pages.checkout.payment.termsCheckboxHint")}
          </p>
        )}

        <div className="flex flex-wrap justify-center gap-2 text-xs font-medium">
          <span className="flex items-center gap-1.5 rounded-lg bg-vm-green-soft px-3 py-1.5 text-vm-green-text">
            <LockClosedIcon className="h-3.5 w-3.5" />
            {t("pages.checkout.payment.securePaymentTitle")}
          </span>
          <span className="flex items-center gap-1.5 rounded-lg bg-vm-primary-soft px-3 py-1.5 text-vm-primary-text">
            <ShieldCheckIcon className="h-3.5 w-3.5" />
            {t("pages.checkout.payment.moneyBackGuaranteeTitle")}
          </span>
        </div>

        <p className="text-center text-xs leading-relaxed text-vm-muted">
          {t("pages.checkout.payment.securePaymentText")}{" "}
          {t("pages.checkout.payment.moneyBackGuaranteeText")}
        </p>
      </div>
    </form>
  )
}
